/**
 * HostAccessControlCenter - one-stop admin control card for managing
 * a user's pilot/host/dashboard/org state. Replaces the multi-panel
 * digging that was needed before.
 *
 * Shows traffic-light status of:
 *   - User
 *   - Pilot intake submitted
 *   - Org linked
 *   - Dashboard (pending_review / active / archived)
 *   - is_host flag
 *
 * One-click actions:
 *   - Activate Dashboard (flips dashboard_status=active and is_host=true)
 *   - Archive / Reactivate Dashboard
 *   - Open Host Dashboard
 *   - Toggle is_pilot / legacy_pilot_free
 */
import { useState, useMemo } from "react";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import {
  CheckCircle2, XCircle, Clock, Zap, Archive, RefreshCw,
  Building2, LayoutDashboard, Crown, ExternalLink, AlertCircle
} from "lucide-react";

const getHeaders = () => {
  const token = localStorage.getItem("session_token") || localStorage.getItem("admin_token");
  return token ? { "Content-Type": "application/json", "Authorization": `Bearer ${token}` } : { "Content-Type": "application/json" };
};

const StatusRow = ({ ok, pending, label, value, testid }) => {
  const Icon = ok ? CheckCircle2 : (pending ? Clock : XCircle);
  const color = ok ? "text-emerald-600" : (pending ? "text-amber-600" : "text-gray-300");
  return (
    <div className="flex items-center gap-2 text-xs" data-testid={testid}>
      <Icon className={`w-4 h-4 ${color} flex-shrink-0`} />
      <span className="text-gray-700 font-medium">{label}</span>
      {value && <span className="text-gray-400 ml-auto truncate max-w-[200px]">{value}</span>}
    </div>
  );
};

export default function HostAccessControlCenter({ user, organizations, linkedEvents, pilotIntakes, onRefresh }) {
  const [busy, setBusy] = useState(false);

  const hostedEvent = useMemo(
    () => (linkedEvents || []).find(e => e.relationship === "host"),
    [linkedEvents],
  );
  const dashStatus = hostedEvent?.dashboard_status || (hostedEvent ? "active" : null);
  const primaryOrg = (organizations || [])[0];
  const hasIntake = (pilotIntakes || []).length > 0;
  const isHost = !!user?.is_host;
  const isPilot = !!user?.is_pilot;
  const plan = user?.plan || user?.subscription_tier || "none";

  const callAdmin = async (path, opts = {}) => {
    setBusy(true);
    try {
      const res = await fetch(`${API}${path}`, { credentials: "include", headers: getHeaders(), ...opts });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(data?.detail || `HTTP ${res.status}`);
      return data;
    } finally {
      setBusy(false);
    }
  };

  const activateDashboard = async () => {
    if (!hostedEvent?.event_id) {
      toast.error("No event linked to activate.");
      return;
    }
    try {
      await callAdmin(`/admin/events/${hostedEvent.event_id}/activate-dashboard`, { method: "POST" });
      toast.success(`Dashboard activated for ${user?.name || user?.email}`);
      onRefresh?.();
    } catch (e) { toast.error(`Activation failed: ${e.message}`); }
  };

  const archiveDashboard = async () => {
    if (!hostedEvent?.event_id) return;
    try {
      await callAdmin(`/admin/events/${hostedEvent.event_id}/archive-dashboard`, { method: "POST" });
      toast.success("Dashboard archived");
      onRefresh?.();
    } catch (e) { toast.error(`Archive failed: ${e.message}`); }
  };

  const togglePilot = async () => {
    try {
      await callAdmin(`/admin/users/${user.user_id}`, {
        method: "PATCH",
        body: JSON.stringify({
          is_pilot: !isPilot,
          plan: !isPilot ? "legacy_pilot_free" : "none",
          stripe_bypass: !isPilot,
        }),
      });
      toast.success(!isPilot ? "Marked as legacy pilot (free for life)" : "Pilot flag removed");
      onRefresh?.();
    } catch (e) { toast.error(`Pilot toggle failed: ${e.message}`); }
  };

  const openHostDashboard = () => {
    if (!hostedEvent?.event_id) return;
    window.open(`/host/${hostedEvent.event_id}`, "_blank");
  };

  const allReady = !!user && hasIntake && !!primaryOrg && dashStatus === "active" && isHost;

  return (
    <div className="border-2 rounded-2xl p-4 mb-4 bg-gradient-to-br from-violet-50 to-blue-50 border-violet-200" data-testid="host-access-control-center">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Crown className="w-4 h-4 text-violet-600" />
          <h3 className="text-sm font-bold text-gray-900">Host Access Control Center</h3>
        </div>
        {allReady ? (
          <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px]">LIVE</Badge>
        ) : (
          <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px]">{dashStatus === "pending_review" ? "PENDING ACTIVATION" : (hostedEvent ? "INCOMPLETE" : "NO DASHBOARD")}</Badge>
        )}
      </div>

      <div className="space-y-1.5 mb-4 bg-white/60 rounded-xl p-3">
        <StatusRow ok={!!user} label="User exists" value={user?.user_id} testid="status-user" />
        <StatusRow ok={hasIntake} label="Pilot intake submitted" value={hasIntake ? `${pilotIntakes.length} intake(s)` : null} testid="status-intake" />
        <StatusRow ok={!!primaryOrg} label="Organization linked" value={primaryOrg ? primaryOrg.name || primaryOrg.display_name : null} testid="status-org" />
        <StatusRow ok={dashStatus === "active"} pending={dashStatus === "pending_review"} label="Dashboard activated" value={hostedEvent ? `${hostedEvent.event_id} · ${dashStatus || "n/a"}` : null} testid="status-dashboard" />
        <StatusRow ok={isHost} label="is_host flag set" value={isHost ? "true" : "false"} testid="status-is-host" />
        <StatusRow ok={isPilot} pending={!isPilot} label="Pilot tier" value={`${isPilot ? "yes" : "no"} · ${plan}`} testid="status-pilot" />
      </div>

      <div className="flex flex-wrap gap-2">
        {dashStatus === "pending_review" && (
          <Button size="sm" disabled={busy} onClick={activateDashboard}
            className="bg-violet-600 hover:bg-violet-700 text-white gap-1.5 h-8 text-xs"
            data-testid="activate-dashboard-btn">
            <Zap className="w-3.5 h-3.5" /> Activate Dashboard Now
          </Button>
        )}
        {dashStatus === "active" && (
          <>
            <Button size="sm" variant="outline" disabled={busy} onClick={openHostDashboard}
              className="gap-1.5 h-8 text-xs" data-testid="open-host-dashboard-btn">
              <ExternalLink className="w-3.5 h-3.5" /> Open Host Dashboard
            </Button>
            <Button size="sm" variant="outline" disabled={busy} onClick={archiveDashboard}
              className="gap-1.5 h-8 text-xs text-amber-700 border-amber-200 hover:bg-amber-50"
              data-testid="archive-dashboard-btn">
              <Archive className="w-3.5 h-3.5" /> Archive
            </Button>
          </>
        )}
        <Button size="sm" variant="outline" disabled={busy} onClick={togglePilot}
          className={`gap-1.5 h-8 text-xs ${isPilot ? "text-emerald-700 border-emerald-200" : "text-gray-600"}`}
          data-testid="toggle-pilot-btn">
          {isPilot ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Crown className="w-3.5 h-3.5" />}
          {isPilot ? "Pilot tier active" : "Mark as legacy pilot"}
        </Button>
        <Button size="sm" variant="ghost" disabled={busy} onClick={onRefresh}
          className="gap-1.5 h-8 text-xs ml-auto" data-testid="refresh-host-access">
          <RefreshCw className={`w-3.5 h-3.5 ${busy ? "animate-spin" : ""}`} /> Refresh
        </Button>
      </div>

      {!hostedEvent && hasIntake && (
        <div className="mt-3 flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-2 text-[11px] text-amber-800" data-testid="warning-no-event">
          <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
          <div>
            Pilot intake exists but no host event has been created yet.
            Use the legacy "Link Host Dashboard" wizard below or have the intake re-processed.
          </div>
        </div>
      )}
    </div>
  );
}
